import React, { FC, useState } from 'react';
import { useModal } from 'store/modal';
import API from 'core/Api';
import { FaTimes } from 'react-icons/fa';
import { useForm } from 'react-hook-form';
import Form from 'components/form/Form';
import FormGroup from 'components/form/FormGroup';
import Button from 'components/Button';
import TooltipWrapper from 'components/TooltipWrapper';
import { useTranslation } from 'react-i18next';
import { createErrorFromResponse } from 'core/Error';
import handlePreviewDoc from 'utils/helpers/handlePreviewDoc';

interface IProps {
  children?: any;
  leaveId: number;
  buttonText: string;
  handlePreview: (leaveId: number) => void;
  invalidate: () => void;
  iconOnly?: boolean;
}

interface IPropsReasonForm {
  handleReject?: any;
  leaveId: number;
}

interface IInputs {
  reason: string;
}

// Reject reason form
const ReasonForm: FC<IPropsReasonForm> = ({ handleReject, leaveId }: IPropsReasonForm) => {
  const methods = useForm<IInputs>();
  const { hideModal } = useModal();
  const { t } = useTranslation();
  return (
    <>
      <p className="text-center">{t('documents.table.confirmRejectReasonLabel')}</p>
      <Form
        methods={methods}
        onSubmit={(data: IInputs) => handleReject(leaveId, data)}
        btnLabel={t('documents.table.confirmRejectDocLabel')}
        primaryActionBtnType="danger"
        onCancel={() => hideModal()}
      >
        <FormGroup
          controlId="formGroupReason"
          as="textarea"
          name="reason"
        />
      </Form>
    </>
  );
};

const RejectLeave: FC<IProps> = ({ leaveId, buttonText, handlePreview, invalidate, iconOnly }: IProps) => {
  const { t } = useTranslation();
  const { showModal } = useModal();
  const [isLoading, setIsLoading] = useState(false);

  // Reject document
  const handleReject = (id: number, data: any) =>
    API.post(`leaves/${id}/reject`, {
      reason: data.reason !== '' ? data.reason : null,
    })
      .then(() => {
        showModal({
          type: 'success',
          content: t('leaves.employer.rejectedLeaveSuccessMsg'),
          onSuccess: () => {},
        });
        invalidate();
      })
      .catch(err =>
        showModal({
          type: 'error',
          content: createErrorFromResponse(err).message,
        })
      );

  // Confirm rejection
  const confirmReject = (id: number) => {
    showModal({
      type: 'form',
      heading: t('documents.table.confirmDocHeading'),
      content: (
        <>
          <p>{t('leaves.employer.confirmRejectLeaveMsg')}</p>
          <Button
            type="button"
            btnType="primary"
            outlined
            disabled={isLoading}
            onClick={() => handlePreviewDoc({ showModal, id, setIsLoading })}
          >
            {t('documents.table.previewDoc')}
          </Button>
          <hr />
          <ReasonForm
            handleReject={handleReject}
            leaveId={id}
          />
        </>
      ),
    });
  };

  if (iconOnly)
    return (
      <div className="d-inline-block ml-2">
        <TooltipWrapper
          id="tooltip-reject"
          overlay={buttonText}
        >
          <FaTimes
            color="EF476F"
            size={16}
            onClick={() => confirmReject(leaveId)}
          />
        </TooltipWrapper>
      </div>
    );

  return (
    <div className="d-inline-block ml-2">
      <TooltipWrapper
        id="tooltip-reject"
        overlay={buttonText}
      >
        <Button
          type="button"
          btnType="danger"
          size="sm"
          onClick={() => confirmReject(leaveId)}
        >
          <FaTimes size={14} />
        </Button>
      </TooltipWrapper>
    </div>
  );
};

export default RejectLeave;
